import React, { useEffect } from "react";
import { Box } from "@material-ui/core";
import H from "history";
import axios from "axios";
import {
  B2B_PORTAL_AUTH_REDIRECT_TO,
  B2B_PORTAL_UNAUTHORIZED_PATH,
  SessionInfo,
  LoginResult,
  Channel,
} from "redmond";
import queryStringParser from "query-string";
import {
  isValidBusinessTravelURL,
  isCorpTenant,
  ONBOARDING_REFERRER_SOURCE,
  getUrlWithUtmParams,
  getPathFromURL,
  useUtmParams,
  travelAppInterface,
  TravelAppInterfaceEventName,
  isVesperTenant,
  isBrexTenant,
  getUserDeviceData,
  setStoredSessionToken,
  setStoredCsrfToken,
  resetBrexAuthRetries,
} from "@capone/common";

import { fetchLogin, postLogin, postLoginCode } from "../../api/v1/auth";
import {
  PATH_B2B_HOME,
  FIRST_TIME_USER_EXPERIENCE_SESSION,
} from "../../utils/paths";
import { LoadingScreen } from "../LoadingScreen";
import { createResponseErrorLog } from "../Logger";
import { config } from "../../api/config";
import { trackEvent } from "../../api/v1/analytics/trackEvent";

interface ILoadingPageProps {
  language: string;
  history: H.History;
}

export const getChannel = (
  queryString: queryStringParser.ParsedQuery
): Channel => {
  const redirectTo = sessionStorage.getItem(B2B_PORTAL_AUTH_REDIRECT_TO);
  const redirectToQueryString = redirectTo
    ? queryStringParser.parseUrl(redirectTo).query
    : {};

  const isEase =
    queryString.isCapitalOneApp === "true" || redirectToQueryString.isCapitalOneApp === "true";
  if (isEase) {
    return "ease";
  }

  const isTravelApp =
    queryString.isTravelApp === "true" ||
    redirectToQueryString.isTravelApp === "true";

  if (isTravelApp) {
    return isVesperTenant(config.TENANT) ? "vesper-app" : "travel-app";
  }

  if (isBrexTenant(config.TENANT)) {
    return "brex-app";
  }

  return getUserDeviceData().deviceType === "mobile" ? "mobile-web" : "desktop";
};

export const LoadingPage = (props: ILoadingPageProps) => {
  const { language, history } = props;
  const utmParams = useUtmParams();

  const redirectToSessionStorageUrl = (url: string) => {
    if (isCorpTenant(config.TENANT)) {
      // Need to `location.replace` because we potentially change React app
      window.location.replace(getPathFromURL(url));
    } else {
      history.push(getPathFromURL(url));
    }

    sessionStorage.removeItem(B2B_PORTAL_AUTH_REDIRECT_TO);
  };

  /*
    The sessionRedirectTo params can be set by the BFF if a user has only access to the
    business portal. In that case, we will redirect them automatically there.
   */
  const handleRedirect = (
    isFirstSession: boolean,
    sessionRedirectTo?: string
  ) => {
    const redirectTo = sessionStorage.getItem(B2B_PORTAL_AUTH_REDIRECT_TO);

    if (
      sessionStorage.getItem("onboarding") === "true" ||
      redirectTo?.includes(ONBOARDING_REFERRER_SOURCE)
    ) {
      window.location.replace(PATH_B2B_HOME);
    } else if (
      sessionRedirectTo &&
      !isCorpTenant(config.TENANT) &&
      isValidBusinessTravelURL(sessionRedirectTo)
    ) {
      const sesionRedirectWithUtm = getUrlWithUtmParams(
        sessionRedirectTo,
        utmParams
      );

      trackEvent({
        eventName: "login_redirect_to_cobt",
        properties: {},
      }).finally(() => window.location.replace(sesionRedirectWithUtm));
    } else if (isFirstSession) {
      history.replace(FIRST_TIME_USER_EXPERIENCE_SESSION);
    } else if (redirectTo) {
      redirectToSessionStorageUrl(redirectTo);
    } else {
      history.replace(PATH_B2B_HOME);
    }
  };

  const hasLoginSucceeded = (login: LoginResult): login is SessionInfo => {
    if ((login as SessionInfo)?.csrfToken) {
      return true;
    }
    return false;
  };

  const handleLoginResponse = (response: LoginResult) => {
    if (hasLoginSucceeded(response)) {
      resetBrexAuthRetries();
      // Only present when Tysons opted this caller into header-based auth (Brex iframe).
      // Must be stored before any redirect, since the next page's first API call needs it.
      if (response.sessionToken) {
        setStoredSessionToken(response.sessionToken);
        // The `H-Csrf-Token` cookie is blocked in the same contexts, so keep the login
        // response's copy too -- userInfo echoes this header back as `csrfToken`, which
        // the corporate app uses as its logged-in signal.
        setStoredCsrfToken(response.csrfToken);
      }
      handleRedirect(response.isFirstSession, response.redirectTo);
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.SESSION_INITIALIZED,
      });
    }
  };

  const handleLoginErrorRedirect = (error: unknown) => {
    if (error) {
      if (axios.isAxiosError(error)) {
        createResponseErrorLog(error);
      }
      history.replace(B2B_PORTAL_UNAUTHORIZED_PATH);
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.SESSION_FAILED,
      });
    }
  };

  const JwtAuthFlow = async (token: string, channel: Channel) => {
    try {
      await fetchLogin();
      const response = await postLogin({ token, channel });
      handleLoginResponse(response);
    } catch (err) {
      handleLoginErrorRedirect(err);
    }
  };

  const AuthZCodeFlow = async (code: string, channel: Channel) => {
    try {
      const response = await postLoginCode({ code, channel });
      handleLoginResponse(response);
    } catch (err) {
      handleLoginErrorRedirect(err);
    }
  };

  useEffect(() => {
    const queryString = queryStringParser.parse(history.location.search);
    const channel = getChannel(queryString);
    // Brex postMessage launch passes the token in router history state, keeping it out of the URL.
    const stateJwt = (history.location.state as { jwt?: unknown } | undefined)
      ?.jwt;

    if (typeof stateJwt === "string" && stateJwt)
      JwtAuthFlow(stateJwt, channel);
    else if (queryString.jwt) JwtAuthFlow(queryString.jwt as string, channel);
    else if (queryString.code) AuthZCodeFlow(queryString.code as string, channel);
    else history.replace(B2B_PORTAL_UNAUTHORIZED_PATH);
  }, []);

  return (
    <Box className="loading-page-container">
      <LoadingScreen language={language} />
    </Box>
  );
};
